import React from 'react';
import { View, Text } from 'react-native';
import { TrendingUp } from 'lucide-react-native';
import { labelColor, scoreToLabel, capitalizeLabel } from '@/lib/scoring/score-display';
import type { VentureLabel } from '@/lib/scoring/methodology';

/** Neutral grey for the "Not scored" state — deliberately NOT a band colour,
 *  so absence never reads as a verdict on the company. */
const NOT_SCORED_TINT = '#94A3B8';

interface IRSScorePillProps {
  /**
   * 0-100 Inverio Readiness Score, or `null` when the startup has no score
   * event. `null` renders an explicit "Not scored" chip — never a number.
   */
  score: number | null;
  /** Canonical venture band from score_event.composite. Falls back to scoreToLabel(score). */
  ventureLabel?: VentureLabel | string;
  size?: 'sm' | 'md';
  /** Show the venture-band label ("Strong") next to the number. */
  showLabel?: boolean;
  /** Show "/100" after the number (only when the label is hidden). */
  showSlash?: boolean;
  showIcon?: boolean;
}

/**
 * Compact canonical IRS score pill — the single representation for list/card
 * surfaces (dealflow, dashboard, startup-detail hero). Colour comes from the
 * canonical venture band (`labelColor`, boundaries 81/66/46/26), NOT the legacy
 * `getScoreColor` traffic-light (70/40). Shows the venture label so the number
 * carries IRS meaning instead of a bare value.
 *
 * ABSENCE IS RENDERED HERE, ONCE. A null score shows "Not scored" in neutral
 * grey. Because this is the single pill used across list/card surfaces, every
 * one of them gets the honest state without its own branch — which is what
 * failed before: a missing score reached `scoreToLabel(0)` and came out as
 * "0 · Critical", the harshest verdict, for companies nobody had scored.
 */
export function IRSScorePill({
  score,
  ventureLabel,
  size = 'sm',
  showLabel = true,
  showSlash = false,
  showIcon = true,
}: IRSScorePillProps) {
  const iconSize = size === 'sm' ? 14 : 16;
  const numClass = size === 'sm' ? 'text-sm' : 'text-lg';

  if (score == null) {
    return (
      <View
        className="flex-row items-center gap-1 px-2 py-1 rounded-lg"
        style={{ backgroundColor: `${NOT_SCORED_TINT}15` }}
        accessibilityLabel="Not scored yet"
      >
        <Text
          style={{ color: NOT_SCORED_TINT }}
          className={`font-semibold ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
          numberOfLines={1}
        >
          Not scored
        </Text>
      </View>
    );
  }

  const band = (ventureLabel ?? scoreToLabel(score)) as VentureLabel | string;
  const tint = labelColor(band);

  return (
    <View
      className="flex-row items-center gap-1 px-2 py-1 rounded-lg"
      style={{ backgroundColor: `${tint}15` }}
    >
      {showIcon && <TrendingUp size={iconSize} color={tint} />}
      <Text style={{ color: tint }} className={`font-bold ${numClass}`}>
        {score}
      </Text>
      {showLabel ? (
        <Text style={{ color: tint }} className="text-xs font-semibold" numberOfLines={1}>
          · {capitalizeLabel(band)}
        </Text>
      ) : (
        showSlash && (
          <Text style={{ color: tint, opacity: 0.7 }} className="text-xs">
            /100
          </Text>
        )
      )}
    </View>
  );
}
